/**
 * penaltyGoal — shared "(P)" predicate for a scored penalty (PMA-COCK-195).
 *
 * Client ask: "Cuando es gol del penal (tiro penal), colocarle (P) a la par
 * de las bolitas de gol" — both on the public match-detail timeline
 * (`PublicMatchTimeline.tsx`) and on the lineup panel's goal chip
 * (`publicRosterStatus.ts` / `PublicTeamSheet.tsx`).
 *
 * A penalty taken IN PLAY is a `Shot` carrying `data.shot_type ===
 * "Penalty"` (`goalFrame.ts`, `constants.ts`, PMA-COCK-175/184 —
 * `useActionFlow.ts`'s `PenaltyKick` quick action). This predicate narrows
 * that further to a SCORED one: `outcome === "Goal"` — a missed penalty
 * carries no goal ball to mark in the first place. Matched
 * case-insensitively and a literal "pen" technique is tolerated, the same
 * loose match every other `shot_type` consumer applies.
 *
 * A penalty-SHOOTOUT kick is never a penalty goal here, and needs no
 * separate exclusion check: a shootout kick is stored as a `SetPiece` with
 * `set_piece_type: "Penalty"` and `data.is_shootout: true`
 * (`penaltyShootout.ts`'s `isShootoutKick`), never as a `Shot` — the
 * `event.type !== "Shot"` guard below already rules it out. It also never
 * moves the score, so it can never appear in a goal list this predicate is
 * run over (`useLiveScore.ts`'s `goalEvents`, `Shot`/`outcome: "Goal"`
 * only).
 */

/** The minimal shape this predicate needs from a goal-like event. */
export interface PenaltyGoalCandidate {
  type: string;
  data?: {
    outcome?: string;
    shot_type?: string;
  } | null;
}

export function isPenaltyGoal(event: PenaltyGoalCandidate): boolean {
  if (event.type !== "Shot") return false;
  const data = event.data ?? {};
  if (data.outcome !== "Goal") return false;
  const shotType = String(data.shot_type ?? "").toLowerCase();
  return shotType === "penalty" || shotType === "pen";
}
